import { DryRunDataSource } from "./dryrun.data-source"
import { HBDataSource } from "./hb.data-source"
import { SupabaseDataSource } from "./supabase.data-source"
import { AoInstance } from "../../ao/ao"
import { PoolMap } from "../../ao/router"
import { PoolInfo } from "../../types/data-source"
import { createLogger } from "../../utils/logger"
import { TokenInfo } from "../../ao/token"

const logger = createLogger("DATA-SOURCE-SERVICE")

export class DataSourceService {
  private supabase: SupabaseDataSource
  private hb: HBDataSource
  private dryRun: DryRunDataSource

  constructor(ao: AoInstance) {
    this.supabase = new SupabaseDataSource(ao)
    this.hb = new HBDataSource(ao)
    this.dryRun = new DryRunDataSource(ao)
  }

  async getPoolsOverview() {
    try {
      logger.log(`Fetching pools overview from s`)
      return await this.supabase.getPoolsOverview()
    } catch (error) {
      logger.error(`Error fetching pools overview from source, falling back to dryrun`, error)
      throw error
      //return await this.dryRun.getPoolsOverview()
    }
  }

  async getReserves(pool: string, pair: [string, string]): Promise<[bigint, bigint, string]> {
    try {
      logger.log(`Fetching reserves from HB`)
      return await this.hb.getReserves(pool, pair)
    } catch (error) {
      logger.error(`Error fetching reserves from HB, falling back to dryrun`, error)
      // logger.log(`Fetching reserves from SB`)
      return await this.supabase.getReservesForPool(pool, pair)
      //return await this.dryRun.getReserves(pool, pair)
    }
  }

  async getPoolByTokens(pair: [string, string], feePercentageBps?: bigint) {
    try {
      logger.log(`Fetching pool by tokens from HB`)
      //throw new Error("Simulating HB failure") // !REMOVE THIS LINE WHEN HB WORKS
      return await this.hb.getPoolByTokens(pair, feePercentageBps)
    } catch (error) {
      logger.error(`Error fetching pool by tokens from HB, falling back to dryrun`, error)
      return await this.dryRun.getPoolByTokens(pair, feePercentageBps)
    }
  }

  async getTokenDetails(tokenProcess: string) {
    return await this.dryRun.getTokenDetails(tokenProcess)
  }

  async getPoolsForToken(tokenId: string) {
    try {
      logger.log(`Fetching suggested pools for token ${tokenId} from Supabase`)
      return await this.supabase.getPoolsForToken(tokenId)
    } catch (error) {
      logger.error(`Error fetching suggested pools from Supabase, falling back to dryrun`, error)
      throw error
    }
  }

  async getGlobalLiquidity() {
    try {
      logger.log(`Fetching global liquidity from Supabase`)
      return await this.supabase.getGlobalLiquidity()
    } catch (error) {
      logger.error(`Error fetching global liquidity from Supabase, falling back to HB`, error)
      throw error
    }
  }

  async getTokensFromAMMFactory() {
    try {
      logger.log(`Fetching tokens from AMM factory`)

      return await this.hb.getTokensFromAMMFactory()
    } catch (error) {
      logger.error(`Error fetching tokens from AMM factory, falling back to dryrun`, error)
      throw error
    }
  }

  async getPoolInfo(poolId: string): Promise<PoolInfo> {
    try {
      console.log(`Fetching pool info for ${poolId} from HB`)
      // throw new Error("Simulating HB failure")
      return await this.hb.getPoolInfo(poolId)
    } catch (error) {
      console.warn(`Error fetching pool info from HB`, error)
      throw error
    }
  }

  async getTokenInfo(tokenId: string): Promise<TokenInfo> {
    try {
      console.log(`Fetching token info for ${tokenId} from HB`)
      // throw new Error("Simulating HB failure")
      return await this.hb.getTokenInfo(tokenId)
    } catch (error) {
      console.warn(`Error fetching token info from HB`, error)
      throw error
    }
  }

  async getBalance(tokenId: string, user: string): Promise<bigint> {
    try {
      logger.log(`Fetching balance for token ${tokenId} and user ${user} from HB`)
      // throw new Error("Simulating HB failure")
      return await this.hb.getBalance(tokenId, user)
    } catch (error) {
      logger.error(`Error fetching balance from HB, falling back to dryrun`, error)
      throw error
      //return await this.dryRun.getBalance(tokenId, user)
    }
  }

  async getPoolMap(): Promise<PoolMap> {
    try {
      logger.log(`Fetching pool map from HB`)
      //throw new Error("Simulating HB failure") // !REMOVE THIS LINE WHEN HB WORKS
      return await this.hb.getPoolMap()
    } catch (error) {
      logger.error(`Error fetching pool map from HB, falling back to dryrun`, error)
      throw error
      // return await this.dryRun.getPoolMap()
    }
  }

  async getApprovedTokens(): Promise<string[]> {
    try {
      logger.log(`Fetching approved tokens from HB`)
      // throw new Error("Simulating HB failure")
      return await this.hb.getApprovedTokens()
    } catch (error) {
      logger.error(`Error fetching approved tokens from HB, falling back to dryrun`, error)
      throw error
      // return await this.dryRun.getApprovedTokens()
    }
  }

  async setCronForProcess(processId: string, every: string): Promise<string> {
    try {
      logger.log(`Setting cron for process ${processId} to ${every}`)
      //throw new Error("Simulating HB failure") // !REMOVE THIS LINE WHEN HB WORKS
      return await this.hb.setCronForProcess(processId, every)
    } catch (error) {
      logger.error(`Error setting cron for process ${processId}`, error)
      throw error
    }
  }
}

let dataSourceInstance: DataSourceService | null = null

export function getDataSource(ao: AoInstance): DataSourceService {
  if (!dataSourceInstance) {
    dataSourceInstance = new DataSourceService(ao)
  }
  return dataSourceInstance
}

export function resetDataSource() {
  dataSourceInstance = null
}
